import ApplicationComponent from "legacy/ApplicationComponent"
import FormGroup from "legacy/FormGroup"
import LegacyButton from "legacy/LegacyButton"
import { centsToDollarsRaw, dollarsToCents } from "legacy/helpers.es6"
import { validatorFactory } from "legacy/validatorFactory"
import _ from "lodash"
import PropTypes from "prop-types"
import React from "react"

import Modal from "src/components/Modal"

// Note: $ and jQuery are loaded globally

class SettlePaymentModal extends ApplicationComponent {
  constructor(props) {
    super(props)

    this.state = {
      showModal: false,
      maxAmount: centsToDollarsRaw(props.amount),
      buttonText: props.buttonText,
      selectedCardId: this.defaultForPaymentType(
        this.defaultPaymentMethodType()
      ),
      saving: false,
      aborting: false,
      paymentType: this.defaultPaymentMethodType(),
      checkNumber: "",
      achMemo: "",
      formError: null,
      amount: centsToDollarsRaw(this.props.amount),
      confirmationAccepted: false,
      sendReceipt: !!this.props.contactEmail,
    }

    this.validator = validatorFactory()
    this.handleOpenModal = this.handleOpenModal.bind(this)
    this.handleCloseModal = this.handleCloseModal.bind(this)
    this.onChangePaymentType = this.onChangePaymentType.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.submit = this.submit.bind(this)
    this.abortPayment = this.abortPayment.bind(this)
  }

  paymentMethodsByType() {
    return _.groupBy(this.props.cards, (paymentMethod) => paymentMethod.type)
  }

  handleOpenModal() {
    this.setState({ showModal: true })
  }

  handleCloseModal() {
    this.setState({
      showModal: false,
      paymentType: this.defaultPaymentMethodType(),
      selectedCardId: this.defaultForPaymentType(
        this.defaultPaymentMethodType()
      ),
    })
  }

  defaultPaymentMethodForSettling() {
    const lastUsedPaymentMethod = this.props.cards.find((paymentMethod) => {
      return (
        paymentMethod.stripePaymentMethodId ===
        this.props.lastUsedPaymentMethodId
      )
    })

    if (lastUsedPaymentMethod !== undefined) return lastUsedPaymentMethod

    if (this.props.reservationDefaultPaymentMethod === null) {
      return null
    }

    // Do this lookup despite having access to the default payment method to ensure it hasn't been deleted
    const reservationDefaultPaymentMethod = this.props.cards.find(
      (paymentMethod) => {
        return (
          paymentMethod.stripePaymentMethodId ===
          this.props.reservationDefaultPaymentMethod.stripePaymentMethodId
        )
      }
    )

    if (reservationDefaultPaymentMethod !== undefined)
      return reservationDefaultPaymentMethod

    return null
  }

  defaultPaymentMethodType() {
    const defaultPaymentMethod = this.defaultPaymentMethodForSettling()

    if (
      defaultPaymentMethod === null &&
      !this.anyCards() &&
      !this.anyBankAccounts()
    ) {
      return "cash"
    } else if (
      defaultPaymentMethod &&
      defaultPaymentMethod.type === "PaymentMethod::Card"
    ) {
      return "credit"
    } else if (
      defaultPaymentMethod &&
      defaultPaymentMethod.type === "PaymentMethod::BankAccount"
    ) {
      return "automated_ach"
    } else if (this.anyCards()) {
      return "credit"
    } else if (this.anyBankAccounts()) {
      return "automated_ach"
    } else {
      return "cash"
    }
  }

  checkSelected() {
    return this.state.paymentType === "check"
  }

  offlineAchSelected() {
    return this.state.paymentType === "ach"
  }

  autopaySelected() {
    return this.cardSelected() || this.automatedAchSelected()
  }

  cardSelected() {
    return this.state.paymentType === "credit"
  }

  automatedAchSelected() {
    return this.state.paymentType === "automated_ach"
  }

  cashSelected() {
    return this.state.paymentType === "cash"
  }

  invalidCreditOption() {
    return this.cardSelected() && this.state.selectedCardId === null
  }

  invalidCreditOptionText() {
    return (
      <span>
        This payment cannot be settled via credit without a credit card on file.
        Please
        <span>
          {" "}
          <a
            className="text-danger underline"
            href={`${this.props.contactPaymentUrl}#payment-methods`}
          >
            add a card to the contact
          </a>{" "}
        </span>
        or reservation and try again.
      </span>
    )
  }

  invalidAutomatedAchOptionText() {
    return (
      <span>
        Payment method is unavailable until the boater adds a bank account to
        Dockwa.
      </span>
    )
  }

  cards() {
    if (this.paymentMethodsByType()["PaymentMethod::Card"] === undefined)
      return []

    return this.paymentMethodsByType()["PaymentMethod::Card"]
  }

  anyCards() {
    return this.cards().length > 0
  }

  bankAccounts() {
    if (this.paymentMethodsByType()["PaymentMethod::BankAccount"] === undefined)
      return []

    return this.paymentMethodsByType()["PaymentMethod::BankAccount"]
  }

  anyBankAccounts() {
    return this.bankAccounts().length > 0
  }

  errorNoOptionsForPaymentType(paymentType) {
    if (paymentType === "credit" && !this.anyCards()) {
      return this.invalidCreditOptionText()
    } else if (paymentType === "automated_ach" && !this.anyBankAccounts()) {
      return this.invalidAutomatedAchOptionText()
    } else {
      return null
    }
  }

  onChangePaymentType(paymentType) {
    const error = this.errorNoOptionsForPaymentType(paymentType)

    if (error === null) {
      this.setState({
        paymentType,
        formError: null,
        selectedCardId: this.defaultForPaymentType(paymentType),
      })
    } else {
      this.setState({
        formError: error,
      })
    }
  }

  // The logic in this method is painfully complex. It is used to determine the "default" payment method
  // for a given payment type. We have to account for the potential that the default payment method is
  // not available in the list of options. If it is not available, we use the first option in the list.
  defaultForPaymentType(paymentType) {
    const defaultPaymentMethod = this.defaultPaymentMethodForSettling()

    switch (paymentType) {
      case "credit": {
        if (
          defaultPaymentMethod &&
          defaultPaymentMethod.type === "PaymentMethod::Card"
        ) {
          return defaultPaymentMethod.stripePaymentMethodId
        }

        const firstCard = this.cards()[0]
        if (firstCard === undefined) return null
        return firstCard.stripePaymentMethodId
      }
      case "automated_ach": {
        if (
          defaultPaymentMethod &&
          defaultPaymentMethod.type === "PaymentMethod::BankAccount"
        ) {
          return defaultPaymentMethod.stripePaymentMethodId
        }

        const firstBankAccount = this.bankAccounts()[0]
        if (firstBankAccount === undefined) return null
        return firstBankAccount.stripePaymentMethodId
      }
      default:
        return null
    }
  }

  submit() {
    if (this.validator.allValid()) {
      this.setState({ saving: true, formError: null })
      const params = {
        payment_method: this.state.paymentType,
        check_number: this.state.checkNumber,
        ach_memo: this.state.achMemo,
        external_card_id: this.state.selectedCardId,
        stripe_payment_method_id: this.state.selectedCardId,
        receipt_behavior:
          (this.props.contactEmail && this.cardSelected()) ||
          this.state.sendReceipt,
        amount: dollarsToCents(this.state.amount),
      }

      $.post(this.props.settleUrl, params, (data) => {
        window.location.reload(false)
      }).fail((data) => {
        this.setState({
          saving: false,
          formError: JSON.parse(data.responseText).error,
        })
      })
    } else {
      this.validator.showMessages()
      this.forceUpdate()
    }
  }

  abortPayment() {
    this.setState({ aborting: true, formError: null })
    const params = {
      amount: 0,
    }

    $.post(this.props.settleUrl, params, (data) => {
      window.location.reload(false)
    }).fail((data) => {
      this.setState({
        aborting: false,
        formError: JSON.parse(data.responseText).error,
      })
    })
  }

  manualMethodText() {
    if (this.cashSelected()) {
      return `in ${this.state.paymentType}`
    } else if (this.checkSelected() || this.offlineAchSelected()) {
      return `by ${this.state.paymentType}`
    }
  }

  renderPaymentMethod() {
    const classWidth =
      this.cashSelected() || this.invalidCreditOption()
        ? "col-xs-12"
        : "col-xs-6"
    return (
      <div className={classWidth}>
        <FormGroup
          inputType="select"
          label="Payment method"
          name="method"
          value={this.state.paymentType}
          onChange={(e) => this.onChangePaymentType(e.target.value)}
        >
          <option key="credit" value="credit">
            Card
          </option>
          <option key="automated_ach" value="automated_ach">
            ACH
          </option>
          <option key="cash" value="cash">
            Cash
          </option>
          <option key="check" value="check">
            Check
          </option>
          <option key="ach" value="ach">
            Offline ACH
          </option>
        </FormGroup>
      </div>
    )
  }

  renderCheckNumber() {
    return (
      <div className="col-xs-6">
        <FormGroup
          inputType="input"
          label="Check number"
          name="checkNumber"
          value={this.state.checkNumber}
          onChange={this.handleInputChange}
        />
      </div>
    )
  }

  renderAchMemo() {
    return (
      <div className="col-xs-6">
        <FormGroup
          inputType="input"
          label="ACH memo"
          name="achMemo"
          value={this.state.achMemo}
          onChange={this.handleInputChange}
        />
      </div>
    )
  }

  renderCardSelector() {
    if (this.cardSelected() && this.anyCards()) {
      return (
        <div className="col-xs-6">
          <FormGroup
            label="Select card"
            labelIcon={
              this.selectedCardPreviouslyFailed() &&
              "fa-solid ml-1 fa-circle-exclamation text-red-600 mr-1"
            }
            labelIconTooltip={
              this.selectedCardPreviouslyFailed() &&
              "A previous payment with this card failed"
            }
            inputType="select"
            name="selectedCardId"
            value={this.state.selectedCardId}
            onChange={this.handleInputChange}
          >
            {_.map(this.cards(), (card) => (
              <option key={card.id} value={card.stripePaymentMethodId}>
                {card.title} {card.subtitle}
              </option>
            ))}
          </FormGroup>
        </div>
      )
    }
  }

  renderBankAccountSelector() {
    if (this.automatedAchSelected() && this.anyBankAccounts()) {
      return (
        <div className="col-xs-6">
          <FormGroup
            label="Select bank account"
            labelIcon={
              this.selectedCardPreviouslyFailed() &&
              "fa-solid ml-1 fa-circle-exclamation text-red-600 mr-1"
            }
            labelIconTooltip={
              this.selectedCardPreviouslyFailed() &&
              "A previous payment with this bank account failed"
            }
            inputType="select"
            name="selectedCardId"
            value={this.state.selectedCardId}
            onChange={this.handleInputChange}
          >
            {_.map(this.bankAccounts(), (card) => (
              <option key={card.id} value={card.stripePaymentMethodId}>
                {card.title} {card.subtitle}
              </option>
            ))}
          </FormGroup>
        </div>
      )
    }
  }

  selectedCardPreviouslyFailed() {
    return this.props.failedCardIds.includes(this.state.selectedCardId)
  }

  renderOutstandingAmount() {
    return (
      <div className="col-xs-6">
        <FormGroup
          inputType="currency"
          label="Outstanding amount"
          name="outstandingAmount"
          value={this.state.maxAmount}
          onChange={this.handleInputChange}
          disabled
        />
      </div>
    )
  }

  renderAmount() {
    return (
      <div className="col-xs-6">
        <FormGroup
          inputType="currency"
          label="Amount to settle"
          name="amount"
          value={this.state.amount}
          onChange={this.handleInputChange}
          validation={this.validator.message(
            "amount",
            this.state.amount,
            `required|max:${this.state.maxAmount},num|min:0.01,num`
          )}
        />
      </div>
    )
  }

  renderConfirmation() {
    let text
    if (this.cardSelected()) {
      text = `Confirm $${this.state.amount} charge to the selected card`
    } else if (this.automatedAchSelected()) {
      text = `Confirm $${this.state.amount} charge to the selected bank account`
    } else {
      text = `Confirm $${this.state.amount} as paid ${this.manualMethodText()}`
    }

    return (
      <div className="col-xs-12">
        <FormGroup
          label={text}
          className="mb-15 mt-0"
          inputType="checkbox"
          name="confirmationAccepted"
          value={this.state.confirmationAccepted}
          onChange={this.handleInputChange}
          validation={this.validator.message(
            "confirmation",
            this.state.confirmationAccepted,
            "required|accepted"
          )}
        />
      </div>
    )
  }

  renderReceiptConfirm() {
    return (
      this.props.contactEmail && (
        <div className="col-xs-12">
          <FormGroup
            label="Email receipt to boater"
            className="mb-15 mt-0"
            inputType="checkbox"
            name="sendReceipt"
            value={this.cardSelected() ? true : this.state.sendReceipt}
            onChange={this.handleInputChange}
            disabled={this.autopaySelected()}
          />
        </div>
      )
    )
  }

  renderAbortPaymentButton() {
    if (this.props.amount) {
      return (
        <LegacyButton
          className="btn btn-danger"
          processing={this.state.aborting}
          onClick={this.abortPayment}
          disabled={this.state.saving}
        >
          <i className="fa-solid fa-trash-can" title="Abort Payment" />
        </LegacyButton>
      )
    }
  }

  renderModalTitle() {
    return `${this.state.buttonText} Payment`
  }

  renderFormErrors() {
    if (this.state.formError) {
      return (
        <div className="col-xs-12 text-danger mb-3">{this.state.formError}</div>
      )
    }
  }

  render() {
    return (
      <div>
        <LegacyButton
          type="secondary"
          className="btn-sm"
          onClick={this.handleOpenModal}
        >
          Settle
        </LegacyButton>

        <Modal
          isOpen={this.state.showModal}
          onClose={this.handleCloseModal}
          size="mediumFixed"
        >
          <Modal.Header title={this.renderModalTitle()} />

          <Modal.Body>
            <p className="mb-5 text-gray-500">
              Please confirm the payment method and amount you&apos;d like to
              settle.
            </p>

            <div className="-mx-2 flex flex-wrap">
              {this.renderPaymentMethod()}
              {this.checkSelected() && this.renderCheckNumber()}
              {this.offlineAchSelected() && this.renderAchMemo()}
              {this.cardSelected() && this.renderCardSelector()}
              {this.automatedAchSelected() && this.renderBankAccountSelector()}
              {this.renderOutstandingAmount()}
              {this.renderAmount()}
              {this.renderReceiptConfirm()}
              {this.renderConfirmation()}
              {this.renderFormErrors()}
            </div>
          </Modal.Body>

          <div className="flex items-center justify-between px-4 pb-4">
            <div className="flex justify-start">
              {this.renderAbortPaymentButton()}
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                className="btn btn-tertiary"
                onClick={this.handleCloseModal}
              >
                Cancel
              </button>

              <LegacyButton
                className={
                  this.invalidCreditOption()
                    ? "btn btn-secondary ml-3"
                    : "btn btn-primary ml-3"
                }
                processing={this.state.saving}
                onClick={this.submit}
                processingText="Settling..."
                disabled={
                  this.invalidCreditOption() ||
                  this.state.aborting ||
                  !this.state.confirmationAccepted
                }
              >
                {this.renderModalTitle()}
              </LegacyButton>
            </div>
          </div>
        </Modal>
      </div>
    )
  }
}

SettlePaymentModal.propTypes = {
  amount: PropTypes.number.isRequired,
  buttonText: PropTypes.string.isRequired,
  cards: PropTypes.array.isRequired,
  contactEmail: PropTypes.string,
  contactPaymentUrl: PropTypes.string,
  failedCardIds: PropTypes.array,
  lastUsedPaymentMethodId: PropTypes.string,
  orderId: PropTypes.number,
  paymentStatus: PropTypes.string,
  reservationDefaultPaymentMethod: PropTypes.object,
  settleUrl: PropTypes.string.isRequired,
}

export default SettlePaymentModal
